import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { TAB_ID, useFocus } from '../../app/focus';
import { toast } from '../../components/toasts';
import { Badge, Button, ErrorText, Field, Input, Modal, Spinner } from '../../components/ui';
import { suggestTicket } from '../../lib/ai';
import { get, patch, post, put } from '../../lib/api';
import { contactLabel, dateTimeOf, formatPhone } from '../../lib/format';
import { ticketLabel, useAttendanceSettings } from '../../lib/queries';
import { FocusResult, TakeContext } from '../../lib/types';
import { Customer, CustomerPicker, decisionBody, decisionError, TicketChoice, TicketChoiceValue } from './TicketFields';

/**
 * "Assumir atendimento" (P3/P5/P10/D7): confere o contato (e-mail técnico editável), o cliente e
 * escolhe o chamado. Ao confirmar, o atendimento já abre na tela (o cronômetro começa).
 */
export function TakeModal({ attendanceId, onClose }: { attendanceId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { expect, adopt } = useFocus();
  const ctx = useQuery({ queryKey: ['take-context', attendanceId], queryFn: () => get<TakeContext>(`/attendances/${attendanceId}/take-context`), staleTime: 0 });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const aiEnabled = Boolean(useAttendanceSettings().data?.ai?.ticketSuggestion);
  const [choice, setChoice] = useState<TicketChoiceValue>({ mode: 'deferred' });
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const d = ctx.data;
    if (!d) return;
    setName(d.contact.name ?? '');
    setEmail(d.contact.email);
    setCustomer(d.contact.agidesk.customer);
    const title = defaultTitle(d);
    setChoice(d.openTickets?.length ? { mode: 'reuse', ticketId: d.openTickets[0].id } : d.services?.length ? { mode: 'create', serviceId: '', title } : { mode: 'deferred' });
  }, [ctx.data]);

  const confirm = async () => {
    const d = ctx.data!;
    // Já tem chamado (ex.: devolvido à fila depois de criado): mantém; trocar fica no "Alterar" do painel.
    const hasTicket = Boolean(d.attendance.ticketId);
    const invalid = hasTicket ? null : decisionError(choice, customer);
    if (invalid) return setError(new Error(invalid));
    setSaving(true);
    setError(null);
    try {
      const changes: Record<string, string> = {};
      if (name.trim() && name.trim() !== (d.contact.name ?? '')) changes.name = name.trim();
      if (email.trim() && email.trim() !== d.contact.email) changes.email = email.trim();
      if (Object.keys(changes).length) await patch(`/contacts/${d.contact.id}`, changes);
      if ((customer?.id ?? null) !== (d.contact.agidesk.customer?.id ?? null)) {
        await put(`/contacts/${d.contact.id}/customer`, { customerId: customer?.id ?? null });
      }
      expect(attendanceId);
      const result = await post<FocusResult>(`/attendances/${attendanceId}/take`, { ticket: hasTicket ? undefined : decisionBody(choice), clientTabId: TAB_ID });
      adopt(result);
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      void qc.invalidateQueries({ queryKey: ['attendance', attendanceId] });
      void qc.invalidateQueries({ queryKey: ['contact', d.contact.id] });
      toast(!hasTicket && choice.mode === 'create' ? 'Atendimento assumido. O chamado está sendo criado no Agidesk.' : 'Atendimento assumido', 'ok');
      onClose();
    } catch (err) {
      setError(err);
      void qc.invalidateQueries({ queryKey: ['attendances'] });
    } finally {
      setSaving(false);
    }
  };

  const d = ctx.data;
  return (
    <Modal
      open
      title="Assumir atendimento"
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => void confirm()} loading={saving} disabled={!d}>
            Assumir e abrir
          </Button>
        </>
      }
    >
      {ctx.isLoading && <Spinner />}
      <ErrorText error={ctx.error} />
      {d && (
        <>
          <h3 className="mb-2 text-sm font-semibold">1. Contato</h3>
          <div className="grid gap-x-3 sm:grid-cols-2">
            <Field label="Nome">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="E-mail" hint={d.contact.emailIsPlaceholder ? '(técnico: troque pelo real, se souber)' : undefined}>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
          </div>
          <div className="-mt-1 mb-3 flex flex-wrap items-center gap-2 text-xs text-fg-soft">
            <span>{formatPhone(d.contact.phones[0])}</span>
            {d.contact.agidesk.contactId ? <Badge tone="ok">já existe no Agidesk</Badge> : <Badge tone="warn">ainda não existe no Agidesk (é criado com o chamado)</Badge>}
            {d.attendance.returning && <Badge tone="accent">retorno</Badge>}
          </div>
          {d.previousAttendances.length > 0 && (
            <p className="mb-3 text-xs text-fg-soft">
              Atendimentos anteriores:{' '}
              {d.previousAttendances
                .map((p) => `${p.lastMessageAt ? dateTimeOf(p.lastMessageAt) : ''}${p.ticketId ? ` (${ticketLabel(p)})` : ''}`)
                .join(' · ')}
            </p>
          )}

          <h3 className="mb-2 text-sm font-semibold">2. Cliente (empresa)</h3>
          <div className="mb-4">
            <CustomerPicker contact={d.contact} value={customer} onChange={setCustomer} />
          </div>

          <h3 className="mb-2 text-sm font-semibold">3. Chamado</h3>
          {d.attendance.ticketId ? (
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm">
              Chamado já vinculado: <b>{ticketLabel(d.attendance)}</b>
              <span className="block text-xs text-fg-soft">Ele continua neste atendimento. Para trocar, use "Alterar" no painel depois de assumir.</span>
            </div>
          ) : (
          <TicketChoice
            value={choice}
            onChange={setChoice}
            openTickets={d.openTickets}
            services={d.services}
            agideskAvailable={d.agideskAvailable}
            defaultTitle={defaultTitle(d)}
            onSuggest={aiEnabled ? () => suggestTicket(attendanceId, customer?.id) : undefined}
          />
          )}
          <ErrorText error={error} />
        </>
      )}
    </Modal>
  );
}

const defaultTitle = (d: TakeContext) =>
  `Atendimento WhatsApp - ${contactLabel({ contactName: d.contact.name, contactPhone: d.contact.phones[0] ?? null })}`;